import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';

import { Button, Alert } from 'react-bootstrap';
import { injectIntl, FormattedMessage } from 'react-intl';

import Spinner from 'react-spinkit';

import Input from 'components/ui/form/Input';

import 'style/components/login/login-form.scss';
import { minLength, uppercase, lowercase, numbers, specialCharacter } from 'util/validation';

/** Component to render a Recover password form */
class RecoverPasswordForm extends Component {

    static propTypes = {
        isResetting:  PropTypes.bool, // True when the form is fetching
        recoverError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // The last happened error
        onSubmit:     PropTypes.func // Listener when the form is submitted
    };

    static defaultProps = {
        recoverSuccess: false
    };

    state = {
        password:        '', // The current text of password field
        passwordConfirm: '' // The current text of password confirm field
    };

    /**
     * Set the initial state
     * Set this to handlers
     */
    componentWillMount() {
        this.handlerSubmit = this.handlerSubmit.bind(this);
        this.handlerPasswordConfirmInput = this.handlerPasswordConfirmInput.bind(this);
        this.handlerPasswordInput = this.handlerPasswordInput.bind(this);
    }

    /**
     * Focus the user field
     */
    componentDidMount() {
        this.refs && this.refs.password && ReactDOM.findDOMNode(this.refs.password).focus();
    }

    /**
     * Do the submit action
     * @param {Event} e The event object
     */
    handlerSubmit(e) {
        e.preventDefault();

        const
            {
                password,
                passwordConfirm
            } = this.state,
            {
                isResetting,
                onSubmit
            } = this.props;

        !isResetting && onSubmit && onSubmit(password, passwordConfirm);
    }

    /**
     * A function that will handle and change the state of the password confirmation input.
     *
     * @param {string} passwordConfirm the confirmation password.
     */
    handlerPasswordConfirmInput(passwordConfirm) {
        this.setState({ passwordConfirm });
    }

    /**
     * Update the username state
     * @param {String} password The new password
     */
    handlerPasswordInput(password) {
        this.setState({ password });
    }

    /**
     * Renders any alert message.
     */
    renderAlert() {
        const { recoverError, isResetting, intl: { formatMessage } } = this.props;
        const min = 12;

        const passwordRequirements = [
            minLength(min),
            uppercase,
            lowercase,
            numbers,
            specialCharacter
        ];
        let errorMessages = [];

        if (recoverError && !isResetting) {
            errorMessages = passwordRequirements.map(validator => {
                const validaiton = validator(this.state.password);


                return validaiton && validaiton.msg ? validaiton.msg : validaiton;
            });
        }

        errorMessages = errorMessages.filter(element => element);

        return recoverError && !isResetting && errorMessages.length > 0 ?
            <Alert bsStyle="danger">{formatMessage({ id: `form.validation.${errorMessages[0]}` }).replace('{min}', min)
            }</Alert> :
            null;
    }

    /**
     * The green success message that will be rendered.
     */
    renderSuccess() {
        const { recoverSuccess, intl: { formatMessage } } = this.props;
        const successMessage = formatMessage({ id: 'label.password_recovering' });

        return recoverSuccess ?
            <Alert bsStyle="success">{successMessage}</Alert> :
            null;
    }

    /**
     * The green success message that will be rendered.
     */
    renderInfoMessage() {
        const { intl: { formatMessage } } = this.props;

        return (
            <Alert bsStyle="info">
                <p>{formatMessage({ id: 'label.password_policy.header' })}</p>
                <ul>
                    <li>{formatMessage({ id: 'label.password_policy.requirement.1' })}</li>
                    <li>{formatMessage({ id: 'label.password_policy.requirement.2' })}</li>
                    <li>{formatMessage({ id: 'label.password_policy.requirement.3' })}</li>
                    <li>{formatMessage({ id: 'label.password_policy.requirement.4' })}</li>
                </ul>
            </Alert>
        );
    }

    /**
     * Renders the confirmation input of the password.
     */
    renderPasswordConfirmInput() {
        const { isResetting, intl: { formatMessage } } = this.props;
        const { passwordConfirm } = this.state;

        return (<Input
            name="passwordConfirm"
            ref="passwordConfirm"
            type="password"
            bsSize="large"
            placeholder={formatMessage({ id: 'title.password_confirm' })}
            glyph="lock"
            value={passwordConfirm}
            disabled={isResetting}
            onChange={this.handlerPasswordConfirmInput}
        />);
    }

    /**
     * Renders the input of the password.
     */
    renderPasswordInput() {
        const { isResetting, intl: { formatMessage } } = this.props;
        const { password } = this.state;

        return (<Input
            name="password"
            ref="password"
            type="password"
            bsSize="large"
            autoFocus
            placeholder={formatMessage({ id: 'title.password' })}
            glyph="lock"
            value={password}
            disabled={isResetting}
            onChange={this.handlerPasswordInput}
        />);
    }

    /**
     * Renders the button with its content.
     */
    renderSubmitButtonInput() {
        const { isResetting } = this.props;

        return (
            <Button
                className="btn-login"
                bsSize="large"
                bsStyle="primary"
                block
                type="submit"
                disabled={isResetting}
            >
                {this.renderButtonContent()}
            </Button>
        );
    }

    /**
     * Renders the submit button content if it is not fetching-, if it is it will render a spinner.
     */
    renderButtonContent() {
        const { isResetting, intl: { formatMessage } } = this.props;

        return isResetting ?
            [
                <Spinner key="login-spinner" name="circle" className="login-spinner" fadeIn="none" />,
                <span key="login-spinner-text" className="login-spinner-text">{formatMessage({ id: 'spinner.loading' })}</span>
            ] :
            formatMessage({ id: 'btn.submit_button' });
    }

    /**
     * Renders the entire form and inner messages.
     */
    renderContent() {
        const resetUrl = '/login';
        const { validKey } = this.props;
        const alertMessage = this.renderAlert();
        const successMessage = alertMessage ? '' : this.renderSuccess();
        const infoMessage = !validKey || successMessage ? '' : this.renderInfoMessage();
        const passwordInput = !validKey || successMessage ? '' : this.renderPasswordInput();
        const passwordConfirmInput = !validKey || successMessage ? '' : this.renderPasswordConfirmInput();
        const submitButtonInput = !validKey || successMessage ? '' : this.renderSubmitButtonInput();
        const backLink = (
            <Link to={resetUrl}>
                <div className="text-muted text-right link-login">
                    <FormattedMessage id="label.return_to_login" />
                </div>
            </Link>
        );

        return (<form onSubmit={this.handlerSubmit}>
            {alertMessage}
            {successMessage}
            {infoMessage}
            {passwordInput}
            {passwordConfirmInput}
            {submitButtonInput}
            {backLink}
        </form>);
    }

    render() {
        const renderContent = this.renderContent();

        return (
            <div>
                <h2 className="mb-xl">
                    <FormattedMessage id="title.recover_password" />
                </h2>
                {renderContent}
            </div>
        );
    }
}

export default injectIntl(RecoverPasswordForm);



// WEBPACK FOOTER //
// ./src/components/login/RecoverPasswordForm.jsx